// AuthContext.js
import { createContext, useContext, useState, useEffect, useCallback } from 'react';

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

// Stati possibili dell'autenticazione
export const AUTH_STATUS = {
  IDLE: 'idle',
  LOADING: 'loading',
  AUTHENTICATED: 'authenticated',
  UNAUTHENTICATED: 'unauthenticated', // Non loggato (401) - stato normale
  ERROR: 'error', // Errore di rete o server (5xx)
};

export const AuthProvider = ({ children }) => {
  const [status, setStatus] = useState(AUTH_STATUS.IDLE);
  const [error, setError] = useState(null);

  const [authInfo, setAuthInfo] = useState({
    apiAuthResponse: null,
    apiAuthName: null,
    apiAuthSurname: null,
    apiAuthGroups: [],
  });

  const [studentInfo, setStudentInfo] = useState({
    matricola: null,
    codFis: null,
    indiRes: null,
    indiDom: null,
  });

  // Fetch token verification
  const verifyToken = useCallback(async () => {
    const response = await fetch(process.env.REACT_APP_URL_AUTH_VERIFYTOKEN, {
      credentials: 'include',
    });

    // 401 = non autenticato, è uno stato valido (non un errore)
    if (response.status === 401) {
      return { authenticated: false };
    }

    if (!response.ok) {
      throw new Error(`Token verification failed: ${response.status}`);
    }

    const data = await response.json();
    return { authenticated: true, ...data };
  }, []);

  // Fetch student address data
  const fetchStudentData = useCallback(async () => {
    const response = await fetch(process.env.REACT_APP_URL_STUDENT_ADDRESS, {
      credentials: 'include',
      method: 'GET',
    });

    if (!response.ok) {
      // Non è un errore critico se non ci sono dati studente
      return null;
    }

    return response.json();
  }, []);

  // Funzione principale di autenticazione
  const authenticate = useCallback(async () => {
    setStatus(AUTH_STATUS.LOADING);
    setError(null);

    try {
      // Step 1: Verifica token
      const tokenData = await verifyToken();

      // Utente non autenticato (401) - stato normale, mostra login
      if (!tokenData.authenticated) {
        setStatus(AUTH_STATUS.UNAUTHENTICATED);
        return;
      }

      if (!tokenData.user || !tokenData.groups) {
        throw new Error('Invalid token response: missing user or groups');
      }

      const { user, groups } = tokenData;
      let finalName = user.firstName;

      // Step 2: Fetch dati studente (parallelo non necessario, ma non blocca se fallisce)
      try {
        const studentData = await fetchStudentData();

        if (studentData && studentData[0]) {
          const student = studentData[0];

          // Se esiste un alias, usalo come nome
          if (student.nomeAlias) {
            finalName = student.nomeAlias;
          }

          setStudentInfo({
            matricola: student.matricola,
            codFis: student.codFis,
            indiRes: {
              viaRes: student.viaRes,
              numCivRes: student.numCivRes,
              capRes: student.capRes,
              comuneRes: student.comuneRes,
              provRes: student.provRes,
              frazRes: student.frazRes,
              regioRes: student.regioRes,
              citstraRes: student.citstraRes,
              nazioneRes: student.nazioneRes,
              forzaSpedPosta: student.forzaSpedPosta,
            },
            indiDom: {
              viaDom: student.viaDom,
              numCivDom: student.numCivDom,
              capDom: student.capDom,
              comuneDom: student.comuneDom,
              provDom: student.provDom,
              frazDom: student.frazDom,
              regioDom: student.regioDom,
              citstraDom: student.citstraDom,
              nazioneDom: student.nazioneDom,
            },
          });
        }
      } catch {
        // Ignora errori sui dati studente - non sono critici
      }

      // Step 3: Aggiorna stato auth
      setAuthInfo({
        apiAuthResponse: user.email,
        apiAuthName: finalName,
        apiAuthSurname: user.lastName,
        apiAuthGroups: groups,
      });

      setStatus(AUTH_STATUS.AUTHENTICATED);
    } catch (err) {
      setError(err.message || 'Authentication failed');
      setStatus(AUTH_STATUS.ERROR);
    }
  }, [verifyToken, fetchStudentData]);

  // Esegui autenticazione al mount
  useEffect(() => {
    authenticate();
  }, [authenticate]);

  // Retry function per errori
  const retry = useCallback(() => {
    authenticate();
  }, [authenticate]);

  const value = {
    // Stato
    status,
    error,
    isLoading: status === AUTH_STATUS.LOADING,
    isAuthenticated: status === AUTH_STATUS.AUTHENTICATED,
    isUnauthenticated: status === AUTH_STATUS.UNAUTHENTICATED,
    isError: status === AUTH_STATUS.ERROR,

    // Dati
    authInfo,
    studentInfo,

    // Azioni
    retry,
    setAuthInfo,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};